<template>
    <app-layout v-if="isPageReady" class="availability-page-layout">
        <div class="availability-page__header">
            <availability-topnav-content
                :currentDate="currentAvailabilityDate"
                :view="availabilityView"
                :onDateChange="handleDateChange"
                :onViewChange="handleViewChange"
                @open-manage="openManageModal"
            ></availability-topnav-content>
        </div>

        <common-content-spacer height="medium">
            <common-page-plain class="availability-page-plain--full">
                <common-content-spacer height="medium">
                    <schedule-form
                        v-if="showModifyAvailability && locations.length"
                        :locations="locations"
                        :dayFrom="7"
                        :dayTo="19"
                        @modify-availability="changeRouteAction"
                    ></schedule-form>
                    <availability-schedule
                        v-else
                        :user="user"
                        :practice="practice"
                        :practiceLocationId="practiceLocationId"
                        :currentDate="currentAvailabilityDate"
                        @modify-availability="changeRouteAction"
                    ></availability-schedule>
                </common-content-spacer>
            </common-page-plain>
        </common-content-spacer>

        <manage-availability-modal
            v-if="showManageModal"
            :userId="user.id"
            :practiceId="practice.id"
            :practiceLocationId="practiceLocationId"
            :locationName="getLocationName()"
            @close="showManageModal = false"
            @saved="onAvailabilitySaved"
        ></manage-availability-modal>
    </app-layout>
</template>

<script>
    import dayjs from 'dayjs'
    import { mapMutations } from 'vuex'
    import PageWithTitleMixin from 'mixins/PageWithTitleMixin'
    import PageWithElementsMixin from 'mixins/PageWithElementsMixin'
    import PageWithDataMixin from 'mixins/PageWithDataMixin'
    import ScheduleForm from 'components/forms/AvailabilityScheduleForm'
    import AvailabilitySchedule from 'components/page-elements/AvailabilitySchedule'
    import AppLayout from 'components/layouts/AppLayout'
    import ManageAvailabilityModal from 'components/page-elements/availability/ManageAvailabilityModal'
    import AvailabilityTopnavContent from 'components/layouts/app-layout/AvailabilityTopnavContent'

    export default {
        mixins: [PageWithTitleMixin, PageWithElementsMixin, PageWithDataMixin],

        components: {
            AppLayout,
            ScheduleForm,
            AvailabilitySchedule,
            ManageAvailabilityModal,
            AvailabilityTopnavContent,
        },

        data() {
            return {
                user: this.$auth.getActiveMember(),
                practice: this.$auth.getPractice(),
                locations: [],
                showManageModal: false,
            }
        },

        computed: {
            pageTitle() {
                return this.showModifyAvailability
                    ? this.$t('pages.availability.index.titles.modify_availability')
                    : this.$t('pages.availability.index.titles.main_location_schedule', { name: this.getLocationName() })
            },

            currentAvailabilityDate() {
                return this.$route.query.date ? dayjs(this.$route.query.date).toDate() : new Date()
            },

            availabilityView() {
                return this.$route.query.view || 'monthly'
            },

            practiceLocationId() {
                return this.$route.query.locationId ? Number(this.$route.query.locationId) : undefined
            },

            showModifyAvailability() {
                return this.$route.params.action === 'modify'
            },
        },

        methods: {
            ...mapMutations('availability', ['SET_SELECT_MODE']),

            handleDateChange(date) {
                this.$router
                    .push({
                        name: this.$route.name,
                        params: this.$route.params,
                        query: {
                            ...this.$route.query,
                            date: dayjs(date).format('YYYY-MM-DD'),
                        },
                    })
                    .catch(() => {})
            },

            handleViewChange(view) {
                this.$router
                    .push({
                        name: this.$route.name,
                        params: this.$route.params,
                        query: {
                            ...this.$route.query,
                            view,
                        },
                    })
                    .catch(() => {})
            },
            getLocations() {
                this.$auth.forceUpdatePractice().then(() => {
                    this.locations = [
                        { label: 'Main', value: '0', isAvailabilitySet: this.$auth.getPractice().isAvailabilitySet },
                        ...this.$auth.getPractice().locations.map((location) => ({
                            label: location.name,
                            value: location.id,
                            isAvailabilitySet: location.isAvailabilitySet,
                        })),
                    ]
                })
            },

            getLocationName() {
                return this.$route.query.locationId
                    ? this.$auth
                          .getPractice()
                          .locations.filter((loc) => loc.id === Number(this.$route.query.locationId))
                          .map((loc) => loc.name)[0]
                    : this.$t('pages.availability.index.breadcrumbs.main_location')
            },

            changeRouteAction(locationId) {
                let practiceLocationId
                if (locationId || locationId === '0') {
                    practiceLocationId = locationId
                } else {
                    practiceLocationId = this.$route.query.locationId
                }
                const action = this.showModifyAvailability ? 'schedule' : 'modify'

                let route = {
                    name: 'availability',
                    params: { action },
                    query: { ...this.$route.query },
                }
                if (practiceLocationId) {
                    route.query.locationId = practiceLocationId
                }

                this.$router.push(route)
            },

            openManageModal() {
                this.showManageModal = true
            },

            onAvailabilitySaved() {
                this.$root.$emit('schedule-changed')
            },

            openAvailabilityForLocation(location) {
                const practiceLocationId = location.value
                const action = this.showModifyAvailability ? 'schedule' : 'modify'

                let route = {
                    name: 'availability',
                    params: { action },
                    query: { ...this.$route.query },
                }
                if (practiceLocationId) {
                    route.query.locationId = practiceLocationId
                }

                this.$router.push(route)
            },
        },

        watch: {
            '$route.query.locationId'(newVal, oldVal) {
                if (newVal !== oldVal) {
                    this.getLocations()
                }
            },
        },

        mounted() {
            this.getLocations()

            if (this.$route.query.manage === '1') {
                this.showManageModal = true
                const query = { ...this.$route.query }
                delete query.manage
                this.$router.replace({ query }).catch(() => {})
            }
        },

        beforeDestroy() {
            this.SET_SELECT_MODE(false)
        },
    }
</script>

<style>
    .mx-datepicker.mx-datepicker-inline,
    .mx-calendar.mx-calendar-panel-date {
        width: 100%;
    }
    .mx-calendar-content .cell.active {
        border-radius: 10px;
        background-color: #13b3e3;
    }
    .mx-calendar-content .cell:hover {
        border-radius: 10px;
    }
    .mx-calendar-content .cell.disabled:hover {
        border-radius: 0;
    }

    /*
     * Availability header rendered at page level (below the standard topnav).
     * Padding mirrors the former app-topnav__avail-content-wrapper; the top
     * border takes over the separation the topnav standard row used to draw.
     */
    .availability-page__header {
        background: #ffffff;
        padding: 0 24px 16px;
        border-top: 1px solid #e5e7eb;
    }

    @media (max-width: 768px) {
        .availability-page__header {
            padding: 0 16px 12px;
        }
    }

    /* Make the white main page area under the schedule header full-bleed for availability page */
    .availability-page-plain--full {
        padding: 0 !important;
        margin: 0 !important;
        background: #ffffff !important;
    }

    .availability-page-plain--full > * {
        padding: 0 !important;
        margin: 0 !important;
    }

    /* Page-specific override: make app content white and full-bleed for availability page */
    .availability-page-layout .app-layout__content {
        background: #ffffff !important;
        padding: 0 !important;
    }

    .availability-page-layout main.app-layout__content {
        padding: 0 !important;
    }

    .availability-page-plain--full.page-plain,
    .availability-page-plain--full .page-plain__content {
        box-shadow: none !important;
        border: none !important;
        padding: 0 !important;
        margin: 0 !important;
    }

    /*
     * Scope the spacer reset to the schedule area only. Targeting
     * .availability-page-layout .content-spacer leaked into portal-mounted
     * modals (which render inside the layout root), flattening their spacing.
     */
    .availability-page-plain--full .content-spacer,
    .availability-page-plain--full .content-spacer > * {
        margin: 0 !important;
        margin-top: 0 !important;
        padding: 0 !important;
    }
</style>
